// shell/components/table/row-action-column.ts
// A column of per-row buttons.
// ============================================================================
//
// A verb that belongs to ONE ROW of a list ("revoke this key", "retry this
// transfer") is a button next to that row — USAGE.md "Where a button belongs":
// one element inside the pane → the pane body. It is not a bare row click: a
// click on a row is a selection gesture, and a list where selecting a row
// destroys it (or spends money) has no safe way to look at anything.
//
// This wraps the table's `custom` column escape hatch (`CustomColumnType`) so
// every list pane that needs row verbs renders the same small button, and so
// the click NEVER reaches the row's own handler underneath.
// ============================================================================

import { createButton } from '../../utils/pane-ui';
import { ColumnRenderer } from './columns/column-base';
import type { CellRenderContext, CellRenderResult, CellUpdateType } from './columns/column-base';
import type { ColumnDef, CustomColumnType } from './types';

/** What a row's button should say — or `null` for "this row has no verb". */
export interface RowActionSpec {
    label: string;
    /** `danger` for anything destructive (the common case here). */
    variant?: 'default' | 'primary' | 'danger';
    disabled?: boolean;
    /** Tooltip. When the action is disabled, this should say why. */
    title?: string;
}

export interface RowActionColumnConfig<T> {
    /** Pure: what this row offers, if anything. */
    plan: (item: T) => RowActionSpec | null;
    /** Runs on press, for the row the button belongs to. */
    execute: (item: T) => void;
    /** Column key (default `actions`) — unique within the column set. */
    key?: string;
    /** Header text. Blank by default: the buttons name themselves. */
    header?: string;
    width?: number;
}

const VARIANT_CLASS = {
    default: 'default',
    primary: 'yes',
    danger: 'no',
} as const;

class RowActionColumnRenderer<T> extends ColumnRenderer<T> {
    readonly supportsGrid = true;
    readonly supportsTable = true;

    constructor(private readonly config: RowActionColumnConfig<T>) {
        super();
    }

    getGridCellClass(): string { return 'cs-column-row-action'; }
    getTableCellClass(): string { return 'cs-column-row-action'; }

    buildGridCell(ctx: CellRenderContext<T>): HTMLElement | null {
        return this.buildButton(ctx);
    }

    buildTableCell(ctx: CellRenderContext<T>): HTMLElement | null {
        return this.buildButton(ctx);
    }

    updateGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement, contentEl: HTMLElement | null, _type: CellUpdateType): CellRenderResult {
        return { contentElement: this.rebind(ctx, cellWrapper, contentEl) };
    }

    updateTableCell(ctx: CellRenderContext<T>, cell: HTMLElement, contentEl: HTMLElement | null, _type: CellUpdateType): CellRenderResult {
        return { contentElement: this.rebind(ctx, cell, contentEl) };
    }

    /**
     * Rows are recycled, so a button built for row 3 can be handed row 40 on
     * the next scroll tick. Rather than patch the old button (and risk it
     * firing for the row it used to show), drop it — listener and all — and
     * build the one this row asks for. A button is cheap; a stale destructive
     * click is not.
     */
    private rebind(ctx: CellRenderContext<T>, cell: HTMLElement, contentEl: HTMLElement | null): HTMLElement | null {
        if (contentEl) {
            this.cleanupElement(contentEl);
            contentEl.remove();
        }
        const next = this.buildButton(ctx);
        if (next) cell.appendChild(next);
        return next;
    }

    private buildButton(ctx: CellRenderContext<T>): HTMLElement | null {
        const spec = this.config.plan(ctx.item);
        if (!spec) return null;
        const item = ctx.item;
        const onClick = (e: MouseEvent) => {
            // The row underneath has its own click handler (open the detail,
            // select the row). A row verb must not trigger it as well.
            e.preventDefault();
            e.stopPropagation();
            this.config.execute(item);
        };
        const btn = createButton({
            label: spec.label,
            variant: VARIANT_CLASS[spec.variant ?? 'default'],
            size: 'small',
            disabled: spec.disabled,
            title: spec.title,
            onClick,
        });
        this.registerCleanup(btn, () => btn.removeEventListener('click', onClick));
        return btn;
    }
}

/**
 * A column of per-row buttons. `plan` decides what (if anything) each row
 * offers and is the piece worth unit-testing; `execute` performs it.
 */
export function createRowActionColumn<T>(config: RowActionColumnConfig<T>): ColumnDef<T> {
    const type: CustomColumnType<T> = {
        kind: 'custom',
        customKind: 'row-action',
        renderer: () => new RowActionColumnRenderer<T>(config),
        supportsGrid: true,
        supportsTable: true,
    };
    return {
        key: config.key ?? 'actions',
        header: config.header ?? '',
        width: config.width ?? 110,
        sortable: false,
        hideable: false,
        alwaysShow: true,
        // Nothing to pick: a column of verbs is part of the list, not a field.
        hideInSelector: true,
        getValue: () => null,
        type,
    };
}
